import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { useSidebar } from "../../context/SidebarContext";
import ProfileSummary from "./ProfileSummary";
import SidebarMenuGroup from "./SidebarMenuGroup";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import CampaignIcon from "@mui/icons-material/Campaign";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import DescriptionIcon from "@mui/icons-material/Description";

import "../../sidebarStyle.css";

const Sidebar = () => {
  const location = useLocation();
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useSidebar();
  const isSidebarOpen = !isSidebarCollapsed;

  const [userRoles, setUserRoles] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState({
    events: false,
    documents: false,
    admin: false
  });

  useEffect(() => {
    const fetchRoles = async () => {
      const user = getAuth().currentUser;
      if (!user) return;
      const t = await user.getIdTokenResult(true);
      const r = Array.isArray(t.claims.roles)
        ? t.claims.roles
        : typeof t.claims.role === "string"
          ? t.claims.role.split(",")
          : (sessionStorage.getItem("userRole") || "").split(",");
      setUserRoles(r);
    };
    fetchRoles();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    setExpandedMenus({
      events: path.startsWith("/events"),
      documents: path.startsWith("/documents"),
      admin: path.startsWith("/admin")
    });
  }, [location.pathname]);

  const hasRole = (r) => userRoles.includes(r);

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({ ...prev, [menu]: !prev[menu] }));
  };

  return (
    <div className={`sidebar ${isSidebarOpen ? "open" : "collapsed"}`}>
      <div className="sidebar-header">
        <Link to="/">
          <img
            src={
              isSidebarOpen
                ? "https://storage.googleapis.com/kinova/Kinova_transparent.png"
                : "https://storage.googleapis.com/kinova/Kinova_iconography.png"
            }
            alt="Kinova Logo"
            className="sidebar-logo"
          />
        </Link>
      </div>

      <div className="sidebar-profile">
        <ProfileSummary showDetails={isSidebarOpen} />
      </div>

      <nav className="sidebar-nav">
        <ul>
          {hasRole("user") && (
            <>
              <li><Link to="/dashboard"><DashboardIcon className="sidebar-icon" />{isSidebarOpen && "Dashboard"}</Link></li>
              <li><Link to="/donors"><PeopleIcon className="sidebar-icon" />{isSidebarOpen && "Donors"}</Link></li>
              <li><Link to="/families"><GroupIcon className="sidebar-icon" />{isSidebarOpen && "Families"}</Link></li>
              <SidebarMenuGroup
                isSidebarOpen={isSidebarOpen}
                icon={EventIcon}
                label="Events"
                basePath="/events"
                submenuOpen={expandedMenus.events}
                setSubmenuOpen={() => toggleMenu("events")}
                submenuItems={[
                  { label: "Events Dashboard", path: "/events/dashboard" },
                  { label: "Manage Events", path: "/events/manage" },
                ]}
              />
              <li><Link to="/marketing"><CampaignIcon className="sidebar-icon" />{isSidebarOpen && "Marketing"}</Link></li>
              <li><Link to="/reports"><BarChartIcon className="sidebar-icon" />{isSidebarOpen && "Reports"}</Link></li>
            </>
          )}
          {hasRole("cereset") && (
            <SidebarMenuGroup
              isSidebarOpen={isSidebarOpen}
              icon={DescriptionIcon}
              label="Documents & Forms"
              basePath="/documents"
              submenuOpen={expandedMenus.documents}
              setSubmenuOpen={() => toggleMenu("documents")}
              submenuItems={[
                { label: "Dashboard", path: "/documents/dashboard" },
                { label: "Annotations", path: "/documents/annotations" },
                { label: "Forms Builder", path: "/documents/builder" },
              ]}
            />
          )}
          {hasRole("admin") && (
            <SidebarMenuGroup
              isSidebarOpen={isSidebarOpen}
              icon={AdminPanelSettingsIcon}
              label="Admin"
              basePath="/admin"
              submenuOpen={expandedMenus.admin}
              setSubmenuOpen={() => toggleMenu("admin")}
              submenuItems={[
                { label: "Dashboard", path: "/admin" },
                { label: "Manage Users", path: "/admin/manage-users" },
                { label: "Audits", path: "/admin/audits" },
              ]}
            />
          )}
        </ul>
      </nav>

      <div className="sidebar-footer">
        <button className="settings-link">
          <Link to="/settings"><SettingsIcon className="sidebar-icon" />{isSidebarOpen && "Settings"}</Link>
        </button>
        <div className="toggle-container">
          <span className="toggle-label">Toggle Sidebar</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={isSidebarOpen}
              onChange={() => setIsSidebarCollapsed(prev => !prev)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;